import { inject, Injectable, signal } from '@angular/core';
import { FuseConfigService } from '@fuse/services/config';
import { Scheme } from '@fuse/services/config/config.types';

/** Claro, oscuro o según el sistema. Espejo del `TemaService` de la app. */
export type Apariencia = 'claro' | 'oscuro' | 'sistema';

export const APARIENCIAS: { id: Apariencia; icono: string; clave: string }[] = [
    { id: 'claro', icono: 'light_mode', clave: 'perfil.aparienciaClaro' },
    { id: 'oscuro', icono: 'dark_mode', clave: 'perfil.aparienciaOscuro' },
    { id: 'sistema', icono: 'brightness_auto', clave: 'perfil.aparienciaSistema' },
];

/**
 * Dónde se recuerda en este navegador. El script de `index.html` lee la misma
 * llave para pintar el fondo correcto antes de que arranque Angular.
 */
export const TEMA_STORAGE_KEY = 'tp_tema';

export function esApariencia(valor: unknown): valor is Apariencia {
    return valor === 'claro' || valor === 'oscuro' || valor === 'sistema';
}

/** La guardada; por defecto **clara** (sin almacenamiento, también). */
export function aparienciaGuardada(): Apariencia {
    try {
        const v = localStorage.getItem(TEMA_STORAGE_KEY);
        if (esApariencia(v)) return v;
    } catch {
        // Sin almacenamiento (modo privado, bloqueado): clara.
    }
    return 'claro';
}

/** Esquema de Fuse: 'auto' sigue a `prefers-color-scheme` (ver LayoutComponent). */
export function esquemaDe(apariencia: Apariencia): Scheme {
    return apariencia === 'oscuro' ? 'dark' : apariencia === 'sistema' ? 'auto' : 'light';
}

/**
 * Apariencia de la web. Sólo vive en el navegador (no es dato de la cuenta ni
 * viaja al back). Reusa el esquema de Fuse: el LayoutComponent pone la clase
 * `light`/`dark` en el body y escucha el modo del sistema en 'auto'.
 */
@Injectable({ providedIn: 'root' })
export class TemaService {
    private _fuseConfig = inject(FuseConfigService);

    readonly actual = signal<Apariencia>(aparienciaGuardada());

    cambiar(apariencia: Apariencia): void {
        if (!esApariencia(apariencia)) return;
        this.actual.set(apariencia);
        this._fuseConfig.config = { scheme: esquemaDe(apariencia) };
        try {
            localStorage.setItem(TEMA_STORAGE_KEY, apariencia);
        } catch {
            // Sin almacenamiento sólo no se recuerda.
        }
    }
}
